import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useDevToolsProtection } from '@/hooks/useDevToolsProtection';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { MODULES, PERMISSIONS } from '@pms/shared';
import { AuthProvider } from '@/contexts/AuthContext';
import { SalaryPassKeyProvider } from '@/contexts/SalaryPassKeyContext';
import { AccountSalaryKeyProvider } from '@/contexts/AccountSalaryKeyProvider';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ProtectedRoute } from '@/components/guards/ProtectedRoute';
import { AppShell } from '@/components/layout/AppShell';
import { queryClient } from '@/lib/queryClient';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/dashboard';
import { ComingSoonPage } from '@/pages/ComingSoonPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { StaffListPage } from '@/pages/staff/StaffListPage';
import { StaffNewPage } from '@/pages/staff/StaffNewPage';
import { StaffDetailPage } from '@/pages/staff/StaffDetailPage';
import { StaffEditPage } from '@/pages/staff/StaffEditPage';
import { RolesListPage } from '@/pages/roles/RolesListPage';
import { RoleDetailPage } from '@/pages/roles/RoleDetailPage';
import { HrmsConfigPage } from '@/pages/hrms/HrmsConfigPage';
import { OrganizationPage } from '@/pages/hrms/OrganizationPage';
import { RecruitmentPage } from '@/pages/recruitment/RecruitmentPage';
import { AttendancePage } from '@/pages/attendance/AttendancePage';
import { AccountPage } from '@/pages/account/AccountPage';
import { MyAttendancePage } from '@/pages/account/MyAttendancePage';
import LeavePage from '@/pages/leave/LeavePage';
import LeaveApprovalsPage from '@/pages/leave/LeaveApprovalsPage';
import { HrmsPayrollPage } from '@/pages/hrms/HrmsPayrollPage';
import { HrmsPenaltyPage } from '@/pages/hrms/HrmsPenaltyPage';
import { TrackingPage } from '@/pages/hrms/TrackingPage';
import { AccountSalaryPage } from '@/pages/account/AccountSalaryPage';
import { MyLoansPage } from '@/pages/account/MyLoansPage';
import { AccountManagePage } from '@/pages/account/AccountManagePage';
import { MyAssetsPage } from '@/pages/account/MyAssetsPage';
import { MyIdsPage } from '@/pages/account/MyIdsPage';
import { AccountPasswordPage } from '@/pages/account/AccountPasswordPage';
import { SopPage } from '@/pages/sop/SopPage';
import { HrmsSopPage } from '@/pages/sop/HrmsSopPage';
import { NoticePage } from '@/pages/notice/NoticePage';
import { HrmsNoticePage } from '@/pages/hrms/HrmsNoticePage';
import { AssetsListPage } from '@/pages/assets/AssetsListPage';
import { AssetsOverviewPage } from '@/pages/assets/AssetsOverviewPage';
import { AssetsMasterPage } from '@/pages/assets/AssetsMasterPage';
import { IdManagementPage } from '@/pages/assets/IdManagementPage';
import { AssetBranchTransfersPage } from '@/pages/assets/AssetBranchTransfersPage';
import { VendorsPage } from '@/pages/finance/VendorsPage';
import { PurchasesPage } from '@/pages/finance/PurchasesPage';
import { LedgerPage } from '@/pages/finance/LedgerPage';
import { MasterPage } from '@/pages/finance/MasterPage';
import { ReimbursementsPage } from '@/pages/finance/ReimbursementsPage';
import { MyReimbursementPage } from '@/pages/finance/MyReimbursementPage';
import { FacultyPaymentsPage } from '@/pages/finance/FacultyPaymentsPage';
import TaskManagementPage from '@/pages/tasks/TaskManagementPage';
import TaskConfigPage from '@/pages/tasks/TaskConfigPage';
import AdditionalTaskPage from '@/pages/tasks/AdditionalTaskPage';
import { GoalsPage } from '@/pages/goals/GoalsPage';
import { CalendarPage } from '@/pages/calendar/CalendarPage';
import { RequirementsListPage } from '@/pages/requirements/RequirementsListPage';
import { RequirementsMasterPage } from '@/pages/requirements/RequirementsMasterPage';
import { RequirementsOverviewPage } from '@/pages/requirements/RequirementsOverviewPage';
import { SubscriptionListPage } from '@/pages/subscription/SubscriptionListPage';
import { SubscriptionOverviewPage } from '@/pages/subscription/SubscriptionOverviewPage';
import { SubscriptionMasterPage } from '@/pages/subscription/SubscriptionMasterPage';
import { TechSupportPage } from '@/pages/tech-support/TechSupportPage';
import { TechSupportDetailPage } from '@/pages/tech-support/TechSupportDetailPage';
import { SupportPage } from '@/pages/support/SupportPage';
import { DesignerPage } from '@/pages/dm-team/DesignerPage';
import { ShootsPage } from '@/pages/dm-team/ShootsPage';
import { DmCalendarPage } from '@/pages/dm-team/DmCalendarPage';
import { ActivitiesPage } from '@/pages/dm-team/ActivitiesPage';
import { StoragePage } from '@/pages/dm-team/StoragePage';
import { DmSettingsPage } from '@/pages/dm-team/DmSettingsPage';
import { ReportsOverviewPage } from '@/pages/reports/ReportsOverviewPage';
import { NotificationsPage } from '@/pages/notifications/NotificationsPage';

export default function App() {
  useDevToolsProtection();
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
          <SalaryPassKeyProvider>
          <AccountSalaryKeyProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/account/me" element={<AccountPage />} />
              <Route path="/account/attendance" element={<MyAttendancePage />} />
              <Route
                path="/account/password"
                element={
                  <ProtectedRoute module={MODULES.ACCOUNT_PASSWORD}>
                    <AccountPasswordPage />
                  </ProtectedRoute>
                }
              />

              {/* Staff Management — Pattern C sibling pages */}
              <Route
                path="/hrms/staff/list"
                element={
                  <ProtectedRoute module={MODULES.HRMS_STAFF}>
                    <StaffListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrms/staff/new"
                element={
                  <ProtectedRoute module={MODULES.HRMS_STAFF}>
                    <StaffNewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrms/staff/:id"
                element={
                  <ProtectedRoute module={MODULES.HRMS_STAFF}>
                    <StaffDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrms/staff/:id/edit"
                element={
                  <ProtectedRoute module={MODULES.HRMS_STAFF}>
                    <StaffEditPage />
                  </ProtectedRoute>
                }
              />

              {/* Roles & Permissions (System) */}
              <Route
                path="/system/roles"
                element={
                  <ProtectedRoute module={MODULES.MASTER_ROLES}>
                    <RolesListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/system/roles/:code"
                element={
                  <ProtectedRoute module={MODULES.MASTER_ROLES}>
                    <RoleDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* HRMS Config — Departments + Designations tabs */}
              <Route
                path="/hrms/config"
                element={
                  <ProtectedRoute module={MODULES.HRMS_ORGANIZATION}>
                    <HrmsConfigPage />
                  </ProtectedRoute>
                }
              />

              {/* Organization hierarchy */}
              <Route
                path="/hrms/organization"
                element={
                  <ProtectedRoute module={MODULES.HRMS_ORGANIZATION}>
                    <OrganizationPage />
                  </ProtectedRoute>
                }
              />

              {/* Recruitment — Pattern B tabbed page + Config sub-page */}
              <Route
                path="/hrms/recruitment"
                element={
                  <ProtectedRoute module={MODULES.HRMS_RECRUITMENT}>
                    <RecruitmentPage />
                  </ProtectedRoute>
                }
              />

              {/* Penalty Approval */}
              <Route
                path="/hrms/penalty"
                element={
                  <ProtectedRoute module={MODULES.HRMS_PENALTY}>
                    <HrmsPenaltyPage />
                  </ProtectedRoute>
                }
              />

              {/* Tracking — desktop screenshot tracker (external service, proxied) */}
              <Route
                path="/hrms/tracking"
                element={
                  <ProtectedRoute module={MODULES.HRMS_TRACKING} permission={PERMISSIONS.TRACKING_VIEW}>
                    <TrackingPage />
                  </ProtectedRoute>
                }
              />

              {/* Attendance — Pattern C: admin view */}
              <Route
                path="/hrms/attendance"
                element={
                  <ProtectedRoute module={MODULES.HRMS_ATTENDANCE}>
                    <AttendancePage />
                  </ProtectedRoute>
                }
              />

              {/* Leave — employee self-service */}
              <Route
                path="/leave"
                element={
                  <ProtectedRoute module={MODULES.LEAVE}>
                    <LeavePage />
                  </ProtectedRoute>
                }
              />

              {/* Leave — manager/admin approvals */}
              <Route
                path="/hrms/leave"
                element={
                  <ProtectedRoute module={MODULES.HRMS_LEAVE}>
                    <LeaveApprovalsPage />
                  </ProtectedRoute>
                }
              />

              {/* Payroll — admin view */}
              <Route
                path="/hrms/payroll"
                element={
                  <ProtectedRoute module={MODULES.HRMS_PAYROLL}>
                    <HrmsPayrollPage />
                  </ProtectedRoute>
                }
              />

              {/* Account Salary — self-service */}
              <Route
                path="/account/salary"
                element={
                  <ProtectedRoute module={MODULES.ACCOUNT_SALARY}>
                    <AccountSalaryPage />
                  </ProtectedRoute>
                }
              />

              {/* Account Loans — self-service, read-only */}
              <Route
                path="/account/loans"
                element={
                  <ProtectedRoute module={MODULES.ACCOUNT_LOANS}>
                    <MyLoansPage />
                  </ProtectedRoute>
                }
              />

              {/* Manage Account — self-service profile edit */}
              <Route
                path="/account/manage"
                element={
                  <ProtectedRoute module={MODULES.ACCOUNT_MANAGE}>
                    <AccountManagePage />
                  </ProtectedRoute>
                }
              />

              {/* My Assets — self-service asset view */}
              <Route
                path="/account/assets"
                element={
                  <ProtectedRoute module={MODULES.ACCOUNT_ASSETS}>
                    <MyAssetsPage />
                  </ProtectedRoute>
                }
              />

              {/* My IDs — self-service view of own saved account IDs */}
              <Route path="/account/ids" element={<ProtectedRoute module={MODULES.ACCOUNT_MY_IDS} permission={PERMISSIONS.MY_IDS_VIEW_OWN}><MyIdsPage /></ProtectedRoute>} />

              {/* SOP — employee view */}
              <Route
                path="/sop"
                element={
                  <ProtectedRoute module={MODULES.SOP}>
                    <SopPage />
                  </ProtectedRoute>
                }
              />

              {/* SOP — admin management */}
              <Route
                path="/hrms/sop"
                element={
                  <ProtectedRoute module={MODULES.HRMS_SOP}>
                    <HrmsSopPage />
                  </ProtectedRoute>
                }
              />

              {/* Notice — employee view */}
              <Route
                path="/notice"
                element={
                  <ProtectedRoute module={MODULES.NOTICE}>
                    <NoticePage />
                  </ProtectedRoute>
                }
              />

              {/* Notice — admin management */}
              <Route
                path="/hrms/notice"
                element={
                  <ProtectedRoute module={MODULES.HRMS_NOTICE}>
                    <HrmsNoticePage />
                  </ProtectedRoute>
                }
              />

              {/* Task Management — unified page (tabs gated per permission) */}
              <Route path="/tasks" element={<ProtectedRoute module={MODULES.TASK}><TaskManagementPage /></ProtectedRoute>} />
              <Route path="/tasks/config" element={<ProtectedRoute module={MODULES.TASK} permission={PERMISSIONS.TASK_CONFIG}><TaskConfigPage /></ProtectedRoute>} />
              <Route path="/additional-task" element={<ProtectedRoute module={MODULES.ADDITIONAL_TASK}><AdditionalTaskPage /></ProtectedRoute>} />

              {/* Goals — single page, tabs gated per permission */}
              <Route path="/goals" element={<ProtectedRoute module={MODULES.GOALS} permission={PERMISSIONS.GOALS_VIEW_OWN}><GoalsPage /></ProtectedRoute>} />

              <Route path="/assets/list" element={<ProtectedRoute module={MODULES.ASSETS_LIST}><AssetsListPage /></ProtectedRoute>} />
              <Route path="/assets/overview" element={<ProtectedRoute module={MODULES.ASSETS_OVERVIEW}><AssetsOverviewPage /></ProtectedRoute>} />
              <Route path="/assets/master" element={<ProtectedRoute module={MODULES.ASSETS_MASTER}><AssetsMasterPage /></ProtectedRoute>} />
              <Route path="/assets/id-management" element={<ProtectedRoute module={MODULES.ASSETS_ID_MANAGEMENT} permission={PERMISSIONS.ID_MANAGEMENT_VIEW}><IdManagementPage /></ProtectedRoute>} />
              <Route path="/assets/branch-transfers" element={<ProtectedRoute module={MODULES.ASSETS_BRANCH_TRANSFERS}><AssetBranchTransfersPage /></ProtectedRoute>} />

              <Route path="/requirements/list" element={<ProtectedRoute module={MODULES.REQUIREMENTS_LIST}><RequirementsListPage /></ProtectedRoute>} />
              <Route path="/requirements/master" element={<ProtectedRoute module={MODULES.REQUIREMENTS_MASTER}><RequirementsMasterPage /></ProtectedRoute>} />
              <Route path="/requirements/overview" element={<ProtectedRoute module={MODULES.REQUIREMENTS_OVERVIEW}><RequirementsOverviewPage /></ProtectedRoute>} />

              <Route path="/subscription/list" element={<ProtectedRoute module={MODULES.SUBSCRIPTION_LIST}><SubscriptionListPage /></ProtectedRoute>} />
              <Route path="/subscription/overview" element={<ProtectedRoute module={MODULES.SUBSCRIPTION_OVERVIEW}><SubscriptionOverviewPage /></ProtectedRoute>} />
              <Route path="/subscription/master" element={<ProtectedRoute module={MODULES.SUBSCRIPTION_MASTER}><SubscriptionMasterPage /></ProtectedRoute>} />

              {/* Finance — Pattern C sibling pages */}
              <Route path="/finance/vendors" element={<ProtectedRoute module={MODULES.FINANCE_VENDORS}><VendorsPage /></ProtectedRoute>} />
              <Route path="/finance/purchases" element={<ProtectedRoute module={MODULES.FINANCE_PURCHASES}><PurchasesPage /></ProtectedRoute>} />
              <Route path="/finance/ledger" element={<ProtectedRoute module={MODULES.FINANCE_LEDGER}><LedgerPage /></ProtectedRoute>} />
              <Route path="/finance/master" element={<ProtectedRoute module={MODULES.FINANCE_MASTER}><MasterPage /></ProtectedRoute>} />
              <Route path="/finance/reimbursements" element={<ProtectedRoute module={MODULES.FINANCE_REIMBURSEMENTS}><ReimbursementsPage /></ProtectedRoute>} />
              <Route path="/reimbursement" element={<ProtectedRoute module={MODULES.FINANCE_REIMBURSEMENT_SELF}><MyReimbursementPage /></ProtectedRoute>} />
              <Route path="/finance/faculty-payment" element={<ProtectedRoute module={MODULES.FINANCE_FACULTY_PAYMENT}><FacultyPaymentsPage /></ProtectedRoute>} />

              {/* Calendar — Pattern A single page */}
              <Route path="/calendar" element={<ProtectedRoute module={MODULES.CALENDAR}><CalendarPage /></ProtectedRoute>} />

              {/* Tech Support — Pattern A single page + detail */}
              <Route path="/tech-support" element={<ProtectedRoute module={MODULES.TECH_SUPPORT}><TechSupportPage /></ProtectedRoute>} />
              <Route path="/tech-support/:id" element={<ProtectedRoute module={MODULES.TECH_SUPPORT}><TechSupportDetailPage /></ProtectedRoute>} />
              <Route path="/support" element={<ProtectedRoute module={MODULES.SUPPORT}><SupportPage /></ProtectedRoute>} />

              {/* Notifications — no module gate, every authenticated user */}
              <Route path="/notifications" element={<NotificationsPage />} />

              {/* Reports Overview */}
              <Route path="/reports/overview" element={<ProtectedRoute module={MODULES.REPORTS_OVERVIEW}><ReportsOverviewPage /></ProtectedRoute>} />

              {/* DM Team — Pattern C sibling pages */}
              <Route path="/dm-team/designer" element={<ProtectedRoute module={MODULES.DM_TEAM_DESIGNER}><DesignerPage /></ProtectedRoute>} />
              <Route path="/dm-team/shoots" element={<ProtectedRoute module={MODULES.DM_TEAM_SHOOTS}><ShootsPage /></ProtectedRoute>} />
              <Route path="/dm-team/calendar" element={<ProtectedRoute module={MODULES.DM_TEAM_CALENDAR}><DmCalendarPage /></ProtectedRoute>} />
              <Route path="/dm-team/activities" element={<ProtectedRoute module={MODULES.DM_TEAM_ACTIVITIES}><ActivitiesPage /></ProtectedRoute>} />
              <Route path="/dm-team/storage" element={<ProtectedRoute module={MODULES.DM_TEAM_STORAGE}><StoragePage /></ProtectedRoute>} />
              <Route path="/dm-team/settings" element={<ProtectedRoute module={MODULES.DM_TEAM_SETTINGS}><DmSettingsPage /></ProtectedRoute>} />

              <Route path="*" element={<ComingSoonPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster richColors position="top-right" />
          </AccountSalaryKeyProvider>
          </SalaryPassKeyProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
